import { ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CategoryValidator } from '@models/models-validators/category-validator';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { ChannelCategory, getExistingRowIds, MAX_CHANNEL_CATEGORIES } from 'icreate/common/models/channel-category';
import { getValidationErrors, ValidationErrorMap } from 'icreate/common/validators';
import _ from 'lodash';
import { Subject, takeUntil } from 'rxjs';
import { VesselService } from '@service/vessel.service';
import { mapErrors } from 'icreate/common/validators/custom-functions';
import { ValidationError } from 'yup';
import { generateNextAvailableNum } from 'icreate/common/models/channel';
import { systemChannelGroupNum } from 'icreate/common/models/system-channels';
import { GenericErrorStrings } from 'icreate/common/validators/validation-errors/generic';

@Component({
    selector: 'app-dialog-channel-category',
    templateUrl: './dialog-channel-category.component.html',
    styleUrls: ['./dialog-channel-category.component.scss'],
    standalone: false,
})
export class DialogChannelCategoryComponent implements OnInit, OnDestroy {
    readonly CATEGORY_ADD = DialogType.add;
    readonly CATEGORY_EDIT = DialogType.edit;
    readonly CATEGORY_DELETE = DialogType.delete;

    isShow: boolean = false;
    channelCategoryList: ChannelCategory[] = [];
    channelCategory: ChannelCategory = new ChannelCategory();
    editDisplayName: string = '';
    selectedCategoryId: string;
    editCategoryNumber: number;
    validationErrors: ValidationErrorMap = {};

    @Output() onAddCategory = new EventEmitter();
    @Output() onEditCategory = new EventEmitter();
    @Output() onDeleteCategory = new EventEmitter();

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            type: DialogType;
            title: string;
            description: string;
            selectedChannelCategory: ChannelCategory;
            isDuplicate: boolean;
        },
        private channelCategoryService: ChannelCategoryService,
        private vesselService: VesselService,
        private cdr: ChangeDetectorRef,
        private dialogRef: MatDialogRef<DialogChannelCategoryComponent>
    ) {}

    ngOnInit(): void {
        this.channelCategoryService.list$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(channelCategoryList => {
                this.channelCategoryList = channelCategoryList;
            });

        if (this.data.isDuplicate) {
            this.editDisplayName = this.data.selectedChannelCategory.category;
            const rowNums = getExistingRowIds(this.channelCategoryList);
            this.editCategoryNumber = generateNextAvailableNum(rowNums);
            this.getErrors();
        } else if (this.data.type === this.CATEGORY_ADD) {
            const rowNums = getExistingRowIds(this.channelCategoryList);
            this.editCategoryNumber = generateNextAvailableNum(rowNums);
        } else {
            this.editCategoryNumber = this.data.selectedChannelCategory.rowId;
            this.editDisplayName = this.data.selectedChannelCategory.category;
            this.selectedCategoryId = this.data.selectedChannelCategory.id;
        }
    }

    ngOnDestroy(): void {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    close() {
        this.dialogRef.close(false);
    }

    saveIsDisabled(): boolean {
        const hasValidationErrors = Object.keys(this.validationErrors)?.length > 0;
        if (this.data.type === this.CATEGORY_ADD)
            return !this.editDisplayName || !this.editCategoryNumber || hasValidationErrors;
        else if (this.data.type === this.CATEGORY_EDIT) {
            return (
                !this.editDisplayName ||
                !this.editCategoryNumber ||
                hasValidationErrors ||
                (this.editDisplayName === this.data.selectedChannelCategory.category &&
                    this.editCategoryNumber === this.data.selectedChannelCategory.rowId)
            );
        }
    }

    async confirmButtonClick() {
        if (this.data.type === this.CATEGORY_DELETE) {
            this.dialogRef.close(true);
            return;
        }

        if (this.saveIsDisabled()) return;
        if (this.data.type === this.CATEGORY_ADD) {
            await this.addChannelCategory();
        } else if (this.data.type === this.CATEGORY_EDIT) {
            this.editChannelGroup();
        } else {
            this.deleteChannelGroup();
        }

        this.dialogRef.close(true);
    }

    async addChannelCategory() {
        let maxRow = Math.max(...this.channelCategoryList.map(category => category.rowId));
        if (!isFinite(maxRow)) maxRow = 0;
        this.onAddCategory.emit({
            ...this.data.selectedChannelCategory,
            category: this.editDisplayName,
            vesselId: this.vesselService.getActiveVesselId(),
            rowId: this.editCategoryNumber,
        });
    }

    async editChannelGroup() {
        this.onEditCategory.emit({
            ...this.data.selectedChannelCategory,
            category: this.editDisplayName,
            rowId: this.editCategoryNumber,
        });
    }

    async deleteChannelGroup() {
        this.onDeleteCategory.emit(this.data.selectedChannelCategory);
    }

    onInputChange(event: { name: string; value: any }) {
        if (event.name === 'category') {
            this.editDisplayName = event.value;
        } else if (event.name === 'rowId') {
            const val = event.value?.toString().trim();
            this.editCategoryNumber = val === '' || val == null ? null : !isNaN(val) ? Number(val) : val;
        }

        this.getErrors();
        this.cdr.detectChanges();
    }

    getErrors() {
        let validation = {
            category: this.editDisplayName,
            rowId: this.editCategoryNumber,
        };
        let rowIds = getExistingRowIds(this.channelCategoryList);
        if (this.data.type === this.CATEGORY_EDIT) {
            rowIds = rowIds.filter(row => row !== this.data.selectedChannelCategory.rowId);
        }
        const errors = getValidationErrors(CategoryValidator(rowIds), validation);

        errors.map(error => {
            if (
                error.path == 'rowId' &&
                [
                    GenericErrorStrings.ABOVE_MAXIMUM,
                    GenericErrorStrings.BELOW_MINIMUM,
                    GenericErrorStrings.GENERIC_INVALID_VALUE,
                ].includes(error.message)
            ) {
                error.message = 'channel.validation.errors.min-max';
                error.params = {
                    ...error.params,
                    min: 1,
                    max: MAX_CHANNEL_CATEGORIES,
                };
            } else if (error.path == 'rowId' && [GenericErrorStrings.NOT_ONE_OF].includes(error.message)) {
                error.message = 'channel.category.form.number.error.duplicate';
                error.params = {
                    ...error.params,
                };
            }

            return error;
        });

        this.validationErrors = mapErrors(errors);

        if (!this.validationErrors?.category) {
            const isDuplicate = this.channelCategoryList.some(
                category => category.category == this.editDisplayName && category.id !== this.selectedCategoryId
            );
            if (isDuplicate) {
                this.validationErrors.category = {
                    message: 'channel.category.form.name.error.duplicate',
                } as ValidationError;
            }
        }
    }

    isSystemGroup() {
        return (
            this.data.type === this.CATEGORY_EDIT && this.data.selectedChannelCategory.rowId === systemChannelGroupNum
        );
    }
}

export enum DialogType {
    add,
    edit,
    delete,
    description,
}
